import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking, session replay, and performance monitoring
 */
export const initSentry = () => {
  // Check if Sentry DSN is configured
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  
  if (!dsn) {
    console.warn('[Sentry] DSN not configured. Error tracking disabled.');
    return;
  }

  const environment = import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_APP_VERSION || 'dev';

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      
      // Session Replay - records user sessions
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: false,
        // Mask all inputs except type="text"
        maskAllInputs: true,
        // Block all media (images, videos)
        blockAllMedia: false,
      }),
      
      // Breadcrumbs for tracking user actions
      Sentry.breadcrumbsIntegration({
        console: true,  // Log console messages
        dom: true,      // Log DOM events (clicks, etc)
        fetch: true,    // Log fetch requests
        history: true,  // Log navigation
        sentry: true,   // Log Sentry events
        xhr: true,      // Log XHR requests
      }),
      
      // HTTP client instrumentation
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [400, 599], // Track failed requests
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of error sessions
    
    // Error filtering
    beforeSend(event, hint) {
      // Filter out certain errors in development
      if (environment === 'development') {
        const error = hint.originalException;
        
        // Ignore ResizeObserver errors (common browser quirk)
        if (error && typeof error === 'string' && error.includes('ResizeObserver')) {
          return null;
        }
      }
      
      return event;
    },
    
    // Performance filtering
    beforeSendTransaction(event) {
      // Don't send transactions for OPTIONS requests
      if (event.request?.method === 'OPTIONS') {
        return null;
      }
      
      return event;
    },
  });
};

/**
 * Set user context for error tracking
 */
export const setSentryUser = (userId: string | null, email?: string) => {
  if (userId) {
    Sentry.setUser({
      id: userId,
      email: email,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Add custom context to all future events
 */
export const setSentryContext = (key: string, value: Record<string, any>) => {
  Sentry.setContext(key, value);
};

/**
 * Clear all user context (on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};
